/**
 * /overlay/chyron?match=<id> — broadcast overlay chyron.
 *
 * T131: a minimal full-screen page intended to be loaded as an OBS
 * browser source over the live video feed. Transparent background;
 * the only visible element is a bottom-bound horizontal banner
 * showing the latest voice-narrated commentary line.
 *
 * Polls the commentary endpoint every 3 seconds. WebSocket upgrade
 * (Daphne live-link) is a follow-up — polling is dead-simple for
 * MVP and the delay is acceptable for a chyron.
 *
 * Match ID comes from `?match=<uuid>`. The page assumes the user is
 * already authenticated (operator opens it in a logged-in browser
 * profile on their streaming PC). Public unauthenticated access via
 * the existing live-link token mechanism is a known follow-up.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../api/axios";
import { formatBallReference } from "../../utils/hundredDisplay";


// How often to refetch the commentary feed. 3s is the sweet spot:
// fast enough for a live broadcast, slow enough not to hammer the
// API. WebSocket upgrade would let us drop this to sub-second.
const POLL_MS = 3000;


export default function Chyron() {
  const [params] = useSearchParams();
  const matchId = params.get("match");
  const [latest, setLatest] = useState(null);  // {text, ball, kind}
  const [err, setErr] = useState("");
  const [matchType, setMatchType] = useState(null);
  const lastSeenRef = useRef("");

  // Fetch the match's format once so the chyron can render Hundred
  // deliveries as "Ball N" instead of over.ball notation.
  useEffect(() => {
    if (!matchId) return;
    let cancelled = false;
    api.get(`/api/matches/matches/${matchId}/scorecard/`)
      .then((r) => { if (!cancelled) setMatchType(r?.data?.match_type || null); })
      .catch(() => { /* chyron degrades gracefully to over.ball */ });
    return () => { cancelled = true; };
  }, [matchId]);

  useEffect(() => {
    if (!matchId) {
      setErr("Missing ?match=<id> query param.");
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/api/matches/${matchId}/commentary/`);
        const results = r?.data?.results || [];
        // results are -ball_number ordered (most recent first). Find
        // the first ball with a voice transcript (preferred caption)
        // OR the first with auto_commentary (template fallback).
        const top = results[0];
        if (!top || cancelled) return;
        const text = top.voice_transcript || top.commentary || "";
        if (!text) return;
        // De-dupe: only re-trigger the slide animation when the text
        // actually changes. Otherwise the banner would re-scroll on
        // every poll even when nothing new happened.
        const key = `${top.id}::${text}`;
        if (key === lastSeenRef.current) return;
        lastSeenRef.current = key;
        setLatest({
          text,
          over: formatBallReference(matchType, { overNumber: top.over_number, ballInOver: top.ball_in_over }),
          kind: top.voice_transcript ? "voice" : "auto",
        });
        setErr("");
      } catch (e) {
        // Quiet on transient failures; only show the error if it
        // persists. Otherwise the overlay flickers on every blip.
        if (!cancelled) setErr(e?.response?.status === 401
          ? "Not authenticated — log in to api.zencricketdata.com first."
          : "");
      }
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [matchId, matchType]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "transparent",
        pointerEvents: "none",
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        color: "#ffffff",
      }}
    >
      {err && (
        <div style={{
          position: "absolute", top: 12, left: 12,
          background: "rgba(220, 38, 38, 0.85)", color: "#fff",
          padding: "6px 12px", borderRadius: 4, fontSize: 12,
        }}>
          {err}
        </div>
      )}
      {latest && (
        <div style={{
          position: "absolute",
          left: 0, right: 0, bottom: 24,
          padding: "0 24px",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{
            background: "linear-gradient(90deg, #6d28d9, #4c1d95)",
            color: "#ffffff",
            fontWeight: 700,
            // clamp() so the chyron stays legible whether OBS captures
            // it at 1920x1080 or someone accidentally opens it on a phone.
            fontSize: "clamp(11px, 1.8vw, 16px)",
            padding: "6px 12px",
            borderRadius: 6,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.5)",
            flexShrink: 0,
          }}>
            OVER {latest.over}
          </span>
          <div
            // Re-mount on text change so the CSS animation restarts
            // and the new line slides in fresh. Without the key, an
            // identical-length-but-different text would just snap.
            key={latest.text}
            style={{
              flex: 1,
              overflow: "hidden",
              background: "rgba(0, 0, 0, 0.7)",
              padding: "10px 16px",
              borderRadius: 6,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.5)",
            }}
          >
            <div
              style={{
                whiteSpace: "nowrap",
                fontSize: "clamp(13px, 2.6vw, 20px)",
                fontWeight: 500,
                animation: "zcdChyronSlide 0.6s ease-out",
              }}
            >
              {latest.text}
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes zcdChyronSlide {
          from { transform: translateX(-100%); opacity: 0; }
          to   { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
